import { useCallback } from "react";
import useSWR from "swr";
import useSWRMutation from "swr/mutation";
import { Paginated, PaginationQuery } from "../../types";
import { buildUrl, mutateCache, useApi } from "../api";
import { CustomError } from "../errors";
import { AdminItemNormativeExclusion, UpsertItemNormativeBodyExclusion } from "./types/itemNormativeExclusions";
import { AdminNormative } from "./types/normatives";

export const useItemNormativeExclusionsApi = () => {
  const api = useApi();

  const getAdminExcludedItem = useCallback(
    (normativeId: string, id: string, signal: AbortSignal) => {
      const url = `/admin/settings/patrol/normatives/${normativeId}/excluded_items/${id}`;
      return useSWRMutation<AdminNormative, CustomError, string>(url, (url) =>
        api.get<AdminNormative>({ url, signal }),
      );
    },
    [api],
  );

  const useAdminExcludedItems = useCallback(
    (normativeId: string, query: PaginationQuery, signal: AbortSignal) => {
      const url = buildUrl(`/admin/settings/patrol/normatives/${normativeId}/excluded_items`, query);
      return useSWR<Paginated<AdminItemNormativeExclusion>, CustomError, string>(url, (url) =>
        api.get<Paginated<AdminItemNormativeExclusion>>({ url, signal }),
      );
    },
    [api],
  );

  const insertAdminExcludedItem = useCallback(
    async (normativeId: string, body: UpsertItemNormativeBodyExclusion): Promise<{ id: string }> => {
      const url = `/admin/settings/patrol/normatives/${normativeId}/excluded_items`;
      const id = await api.post<{ id: string }, UpsertItemNormativeBodyExclusion>(url, body);
      mutateCache([`/admin/settings/patrol/normatives/${normativeId}/excluded_items`]);
      return id;
    },
    [api],
  );

  const updateAdminExcludedItem = useCallback(
    async (normativeId: string, itemId: string, body: UpsertItemNormativeBodyExclusion): Promise<void> => {
      const url = `/admin/settings/patrol/normatives/${normativeId}/excluded_items/${itemId}`;
      await api.put<UpsertItemNormativeBodyExclusion>(url, body);
      mutateCache([`/admin/settings/patrol/normatives/${normativeId}/excluded_items`]);
    },
    [api],
  );

  const deleteAdminExcludedItem = useCallback(
    async (normativeId: string, itemId: string): Promise<void> => {
      const url = `/admin/settings/patrol/normatives/${normativeId}/excluded_items/${itemId}`;
      await api.del(url);
      mutateCache([
        `/admin/settings/patrol/normatives/${normativeId}/excluded_items`,
        `/admin/settings/patrol/normatives/${normativeId}/excluded_items/${itemId}`,
      ]);
    },
    [api],
  );

  return {
    getAdminExcludedItem,
    useAdminExcludedItems,
    insertAdminExcludedItem,
    updateAdminExcludedItem,
    deleteAdminExcludedItem,
  };
};
